import { useCallback, useRef, useSyncExternalStore } from 'react';

import { subscribeToPlayheadUiTicks, type TelemetryReplayControls } from './useTelemetryReplay';

export type LivePlayheadSample = {
  timecodeMs: number;
  elapsedSec: number;
};

/** Sample playhead in sync with the single replay clock — no extra rAF loops. */
export function useLivePlayhead(
  replay: TelemetryReplayControls,
  enabled = true,
): LivePlayheadSample {
  const getPlayheadTimecodeMs = replay.getPlayheadTimecodeMs;
  const getElapsedSec = replay.getElapsedSec;
  const snapshotRef = useRef<LivePlayheadSample>({
    timecodeMs: getPlayheadTimecodeMs(),
    elapsedSec: getElapsedSec(),
  });

  const getSnapshot = useCallback((): LivePlayheadSample => {
    const timecodeMs = getPlayheadTimecodeMs();
    const elapsedSec = getElapsedSec();
    const cached = snapshotRef.current;

    if (cached.timecodeMs === timecodeMs && cached.elapsedSec === elapsedSec) {
      return cached;
    }

    const next = { timecodeMs, elapsedSec };
    snapshotRef.current = next;
    return next;
  }, [getElapsedSec, getPlayheadTimecodeMs]);

  return useSyncExternalStore(
    enabled ? subscribeToPlayheadUiTicks : () => () => {},
    getSnapshot,
    getSnapshot,
  );
}
